<template lang="pug">
#job-site-step.wow__step.wow__step--second
  .row
    .col-sm-12
      ValidationProvider(
        v-slot="{ errors }"
        ref="addressValidator"
        vid="address"
        :rules="addressValidatorRules"
        slim
      )
        .form-group.wo-js-address-group(:class="{ 'has-error': !!errors[0] }")
          label.control-label.required(for="wo-js-address")
            | Address
            abbr(title="required") *
          div(
            v-tooltip
            :title="readonly ? 'Address cannot be changed' : ''"
          )
            GmapAutocomplete#wo-js-address.form-control(
              v-model="addressProxy"
              :disabled="readonly"
              select-first-on-enter
              placeholder="Search for an address"
              @place_changed="onAddressChange"
              @input="onAddressInput"
              @blur.native="blurAddress()"
            )
            span.help-block {{ errors[0] }}
  .row
    .col-sm-12(v-show="!showJobSiteEmptyState")
      ValidationProvider(
        v-slot="{ errors }"
        name="radius"
        :rules="`required_if:address|gte:${jobSiteMinimumRadius}`"
        slim
      )
        .form-group.wo-js-radius-group(:class="{ 'has-error': !!errors[0] }")
          .d-flex.justify-content-between
            label.control-label(for="wo-js-radius")
              | Radius
            small.radius {{ form.radius }} meters
          // eslint-disable
          AppRangeSlider#wo-js-radius(
            v-model.number="form.radius"
            :step="10"
            :max="3000"
            :min="jobSiteMinimumRadius"
          )
          span.help-block {{ errors[0] }}
          JobSiteSmallRadiusWarning.mt-15(:radius="form.radius")
  template(v-if="showJobSiteEmptyState")
    #wo-js-map.empty.d-flex.flex-column.align-items-center.justify-content-center
      img.empty-image(
        :src="IMAGE_WORK_ORDERS_MAP"
        alt="No job site"
        width="120"
      )
      h5.mb-10.mt-20 No address
      p Please enter the address for the job site

  JobSitesMap#wo-js-map(
    v-show="!showJobSiteEmptyState"
    :class="{ readonly }"
    :viewport="mapViewport"
    :position="jobSitePosition"
    :zoom-level="zoomLevel"
    :target-job-site="targetJobSite"
    :map-options="mapOptions"
    :geolocation-control="false"
    :enable-clear-map-center="false"
    @click="newMarkerPosition"
    @bounds-changed="updateMapBounds"
    @viewport-adjustment-to-fit-radius="updateMapViewport"
  )
    AppMapMarker(
      v-if="hasJobSite"
      has-icon
      is-draggable
      is-clickable
      :marker="jobSiteProxy"
      show-marker-radius
      :icon-url="markerIconUrl"
      :circle-options="jobSiteCircle"
      :icon-size="primaryIconSize(28, 40)"
      @update="newMarkerPosition"
    )
  .row
    .col-sm-12
      ValidationProvider(
        v-slot="{ errors }"
        vid="jb-on-arrival"
        rules=""
        slim
      )
        .form-group.wo-js-on-arrival-group.mb-0(:class="{ 'has-error': !!errors[0] }")
          // eslint-disable
          AppToggleButtonGroup(
            v-model="form.enter_action"
            label="On arrival"
            :items="JOB_SITE_ENTER_ACTIONS"
            :selected="form.enter_action"
            @input="onEnterActionSelect"
          )
          span.help-block(v-if="errors[0]") {{ errors[0] }}
  .row
    .col-sm-12
      ValidationProvider(
        v-slot="{ errors }"
        vid="jb-on-departure"
        rules=""
        slim
      )
        .form-group.wo-js-on-departure-group.mb-0(:class="{ 'has-error': !!errors[0] }")
          // eslint-disable
          AppToggleButtonGroup(
            v-model="form.exit_action"
            label="On departure"
            :items="JOB_SITE_EXIT_ACTIONS"
            :selected="form.exit_action"
            @input="onExitActionSelect"
          )
          span.help-block(v-if="errors[0]") {{ errors[0] }}
</template>

<script>
/* eslint-disable vue/no-mutating-props */
import { pick } from 'lodash-es';
import flushPromises from 'flush-promises';
import { mapInertiaProps } from '~/utils/inertia';
import JobSitesMap from '../../job_sites/JobSitesMap.vue';
import JobSiteSmallRadiusWarning from '../../job_sites/JobSiteSmallRadiusWarning.vue';
import { IMAGE_WORK_ORDERS_MAP } from '~/rails_assets';
import { primaryIconSize, WorkOrdersGeolocate as GeolocateMixin } from '~/mixins';
import { JOB_SITE_ACTIONS, JOB_SITE_DEFAULT_RADIUS, JOB_SITE_ENTER_ACTIONS, JOB_SITE_EXIT_ACTIONS } from '~/app_constants';
import generateCustomColorMarker from '~/utils/generate_custom_color_marker';

export const JOB_SITE_KEYS = ['address', 'radius', 'position', 'enter_action', 'exit_action'];

// FIXME: Refactor common map logic (JobSites and Here) with Composition API
export default {
  name: 'WorkOrderSiteStep',
  components: {
    JobSitesMap,
    JobSiteSmallRadiusWarning,
  },
  mixins: [primaryIconSize, GeolocateMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    address: {
      type: String,
      default: () => null,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      default: () => true,
    },
    form: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      visited: 0,
      zoomLevel: 15, // Default zoomLevel
      mapBounds: null,
      mapViewport: null,
      markerIconUrl: generateCustomColorMarker('#2AA7FF'),
      addressProxy: this.form.address || null,
      JOB_SITE_ACTIONS,
      JOB_SITE_ENTER_ACTIONS,
      JOB_SITE_EXIT_ACTIONS,
      IMAGE_WORK_ORDERS_MAP,
      defaultIconSize: { width: 15, height: 15 },
    };
  },
  computed: {
    ...mapInertiaProps({
      jobSiteMinimumRadius: ({ job_site_minimum_radius: jsmr }) => jsmr || JOB_SITE_DEFAULT_RADIUS,
      jobSiteDefaultRadius: ({ job_site_default_radius: jsdr }) => jsdr || JOB_SITE_DEFAULT_RADIUS,
    }),
    addressValidatorRules() {
      return {
        required: !this.editing && this.visible,
      };
    },
    mapOptions() {
      return {
        zoomControl: !this.readonly,
        mapTypeControl: !this.readonly,
        disableDefaultUI: this.readonly,
      };
    },
    jobSiteProxy() {
      return pick(this.form, JOB_SITE_KEYS);
    },
    targetJobSite() {
      return !this.hasJobSite ? null : this.jobSiteProxy;
    },
    jobSitePosition() {
      return !this.hasJobSite ? null : this.jobSiteProxy.position;
    },
    jobSiteCircle() {
      return {
        fillColor: '#b6defc',
        strokeColor: '#2AA7FF',
      };
    },
    hasJobSite() {
      return this.jobSiteExists(this.jobSiteProxy);
    },
    showJobSiteEmptyState() {
      // TODO: Make sure this works properly
      return !this.hasJobSite && !this.form.address;
    },
    iconSize() {
      if (this.zoomLevel > 13) {
        return {
          width: this.zoomLevel,
          height: this.zoomLevel,
        };
      }

      return this.defaultIconSize;
    },
  },
  watch: {
    // REFACTOR: Simplify this to be more Vue standard
    visible: {
      immediate: true,
      async handler(value) {
        if (value) {
          this.visited += 1;
        }

        // Little hack to make the job site appear centered in the map
        // when opening the edit work order dialog with a marker.
        if (value) {
          this.form.radius += 1;
          setTimeout(() => {
            this.form.radius -= 1;
          }, 50);
        }

        if (value && this.form.address && !this.readonly) {
          const position = await this.getClientAddressPosition(this.form.address);

          this.form.position = position;
          this.addressProxy = this.form.address;

          return;
        }

        if (value && this.editing && this.form.address) {
          this.addressProxy = this.form.address;
        }

        if (value && !this.readonly) {
          if (this.visited === 1) {
            this.addressProxy = null;
          }
        }
      },
    },
  },
  methods: {
    async onAddressInput(event) {
      this.addressProxy = event.target.value;
    },
    async blurAddress() {
      if (!this.addressProxy || !this.addressProxy.length) {
        return;
      }

      if (this.form.address && this.addressProxy !== this.form.address) {
        this.form.address = this.addressProxy;
        await flushPromises();
        this.$refs.addressValidator.applyResult({
          valid: false,
          failedRules: {},
          errors: ['select an address'],
        });
      }
    },
    async onAddressChange(place) {
      if (place && place.geometry && place.geometry.location) {
        const position = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        this.mapBounds = null;
        this.form.position = position;
        this.form.address = place.formatted_address;
        this.form.radius = this.form.radius || this.jobSiteDefaultRadius || JOB_SITE_DEFAULT_RADIUS;
        this.addressProxy = place.formatted_address;
      }
    },
    async newMarkerPosition(event) {
      if (event.latLng && event.latLng.lat && event.latLng.lng) {
        const position = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        };

        this.mapBounds = null;
        this.form.position = position;
        const result = await this.getClientAddress(position);

        if (result) {
          this.addressProxy = result.formatted_address;
          this.form.address = result.formatted_address;
        }
      }
    },
    async updateMapBounds(bounds) {
      this.mapBounds = bounds;
      this.setMapViewport();
    },
    async updateMapViewport(status) {
      if (status === 'ready') {
        this.setMapViewport();
      }
    },
    async setMapViewport() {
      this.mapViewport = this.mapBounds;
      await this.$nextTick();
      // afer the main map's viewport has moved to the replicate the wizard map's viewport...
      // ... unset the main map's viewport so that changes to the wizard map's viewport don't propagate anymore
      this.mapViewport = null;
    },
    onEnterActionSelect(action) {
      this.form.exit_action = action;
    },
    onExitActionSelect(action) {
      if (action === JOB_SITE_ACTIONS.trackTime) {
        this.form.enter_action = action;
      }
    },
    jobSiteExists(jobSite) {
      return (
        jobSite &&
        jobSite.position &&
        typeof jobSite.position.lat === 'number' &&
        typeof jobSite.position.lng === 'number'
      );
    },
  },
};
/* eslint-enable vue/no-mutating-props */
</script>
